import { useMemo } from 'react'

import { DashboardListCreationRestControllerService } from '@api'
import { useUnit } from 'effector-react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { FormField } from '@/components/ui/form/form-field'
import { SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs'
import { useLists } from '@/modules/chatting/hooks/use-lists'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils'
import { ListsDropdown } from '@/modules/chatting/ui/sidebar/lists-dropdown'
import { handleError } from '@/utils/error'

import { DIALOG_TYPE } from '../constants'
import { DialogProps } from '../types'
import {
  CreateOpenChatsListFormValues,
  createOpenChatsListFormResolver,
} from './validation'

export const CreateOpenChatsListDialog = ({ isOpen, onClose }: DialogProps) => {
  const form = useForm<CreateOpenChatsListFormValues>({
    resolver: createOpenChatsListFormResolver,
    defaultValues: {
      listSelectionType: 'new',
    },
    mode: 'onChange',
  })
  const listSelectionType = form.watch('listSelectionType')
  const creatorId = useUnit($creatorId)
  const startOpenChatsListCreationProcessMutation = useMutation(
    ({
      minutes,
      listId,
      newListName,
    }: Required<CreateOpenChatsListFormValues>) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }
      if (listSelectionType === 'new') {
        return DashboardListCreationRestControllerService.createNewOpenChatsList(
          {
            creatorId,
            requestBody: { minuteThreshold: minutes, name: newListName },
          },
        )
      }

      return DashboardListCreationRestControllerService.createExistingOpenChatsList(
        {
          creatorId,
          requestBody: { listId, minuteThreshold: minutes },
        },
      )
    },
    {
      onSuccess: ({ listId }, { listName, newListName, listSelectionType }) => {
        const name = listSelectionType === 'new' ? newListName : listName

        onClose()
        if (listId && name) {
          openDialog({
            type: DIALOG_TYPE.OPEN_CHATS_LIST_CREATION_PROGRESS,
            listId,
            listName: name,
          })
        }
      },
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while creating open chats list',
        )
      },
    },
  )

  const { lists, isLoading, canLoadMore, isFetchingNextPage, onLoadMore } =
    useLists('all-lists', {
      enabled: listSelectionType === 'existing' && isOpen,
    })

  const filteredLists = useMemo(
    () => lists.filter((list) => list.type === 'custom'),
    [lists],
  )

  const onSubmit = (values: CreateOpenChatsListFormValues) => {
    return startOpenChatsListCreationProcessMutation.mutateAsync(
      values as Required<CreateOpenChatsListFormValues>,
    )
  }

  const listName = form.watch('listName')

  return (
    <Dialog
      bigDialog
      show={isOpen}
      onClose={onClose}
      panelClassName="lg:max-w-[700px]"
      afterLeave={form.reset}
    >
      <form
        className="space-y-6"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Dialog.Header>
          <Dialog.Title>Create Open Chats List</Dialog.Title>
          <Dialog.Description className="mx-auto block max-w-[580px] text-center">
            Mass messages should feel natural and not like mass messages - be
            more personal by excluding people that you chatted with within the
            last hours.
            <br /> Create a list with chats that include messages 1 to X minutes
            ago.
          </Dialog.Description>
        </Dialog.Header>
        <FormProvider {...form}>
          <FormField
            name="minutes"
            label="Minutes"
            type="number"
            showErrors
          />
          <Controller
            name="listSelectionType"
            control={form.control}
            render={({ field: { onChange, value } }) => (
              <Tabs
                value={value}
                onValueChange={onChange}
                className="mt-6 flex w-full flex-col justify-center"
              >
                <TabsList className="grid w-full grid-cols-2 self-center">
                  <TabsTrigger value="new">Create new</TabsTrigger>
                  <TabsTrigger value="existing">Select existing</TabsTrigger>
                </TabsList>

                <TabsContent
                  value="new"
                  className="mt-6"
                >
                  <FormField
                    label="Name your open chats list"
                    name="newListName"
                    showErrors
                  />
                </TabsContent>
                <TabsContent
                  value="existing"
                  className="mt-6 w-full"
                >
                  <Controller
                    name="listId"
                    control={form.control}
                    render={({ field: { onChange, value } }) => (
                      <div className="flex flex-col space-y-2">
                        <label className="text-text-default text-sm font-medium">
                          Choose a list
                        </label>
                        <ListsDropdown
                          items={filteredLists}
                          onSelect={(id) => {
                            const listName = lists.find(
                              (list) => list.id === id,
                            )?.name

                            form.setValue('listName', listName)
                            onChange(id)
                          }}
                          canLoadMore={canLoadMore}
                          onLoadMore={onLoadMore}
                          isLoadingMore={isFetchingNextPage}
                          disabled={isLoading}
                          selectProps={{
                            value,
                          }}
                          trigger={() => {
                            return (
                              <SelectTrigger className="text-secondary-500 w-full text-base">
                                {listName ? (
                                  unescapeHtmlText(listName)
                                ) : (
                                  <SelectValue placeholder="Select a list" />
                                )}
                              </SelectTrigger>
                            )
                          }}
                        />
                      </div>
                    )}
                  />
                </TabsContent>
              </Tabs>
            )}
          />
          <Dialog.ClosableFooter onClose={onClose}>
            <Button
              className="w-full"
              disabled={!form.formState.isValid}
              isLoading={form.formState.isSubmitting}
            >
              Save
            </Button>
          </Dialog.ClosableFooter>
        </FormProvider>
      </form>
    </Dialog>
  )
}
